<template>
  <v-form v-if="loadData" ref="form" :class="{'py-4': isMobile, 'py-5': !isMobile}">
    <div>
      <!--SERVICE INFORMATION-->
      <h3>Service Information</h3>
      <service-information
        :v="$v.student.form.serviceInformation"
        :absentTypesArray="absentTypesArray"
        :absentReasonsArray="absentReasonsArray"
        :absentAlternativesArray="absentAlternativesArray"
        :groupTypeArray="groupTypeArray"
        :deliveryModeArray="deliveryModeArray"
        :serviceDeliveredArray="serviceDeliveredArray"
        :student="student"
        :showTooltip="showTooltip"
        :action="action"
        :supervisorFlagsArray="supervisorFlagsArray"
        v-on:calculate:minutes="calculateMinutes"
      />
      <!--MEDICAID-->
      <template v-if="showMedicaid">
        <v-layout row wrap :class="{'py-1': isMobile, 'pt-3': !isMobile}" style="padding-bottom: 5px;">
          <v-flex xs12 sm8>
            <h3>Documentation Of Service and Student Performance</h3>
          </v-flex>
        </v-layout>
        <extended-service-information
          :action="action"
          :deliveryModeArray="deliveryModeArray"
          :groupTypeArray="groupTypeArray"
          :absentTypesArray="absentTypesArray"
          :absentReasonsArray="absentReasonsArray"
          :absentAlternativesArray="absentAlternativesArray"
          ref="extendedServiceInformation"
          :student="student"
          :supervisorFlagsArray="supervisorFlagsArray"
          :v="$v.student.form.serviceInformation"
          relevant-student-behavior />
        <!--Relevant Student Behavior Details-->
        <v-textarea
          label="Relevant Student Behavior Details"
          placeholder="Enter Relevant Student Behavior Details"
          v-model="student.form.serviceInformation.studentBehavior"
          @blur="handleTextTrimming('studentBehavior', student.form.serviceInformation.studentBehavior)"
          :error-messages="studentBehaviorErrors">
        </v-textarea>
        <v-layout row wrap :class="{'py-1': isMobile, 'pt-3': !isMobile}">
          <v-flex xs12 sm8 />
          <v-flex xs12 sm4>
            <v-text-field
              label="Total Reimbursable Minutes"
              readonly
              :class="{'mt-4': isMobile, 'mt-0 pt-0': !isMobile}"
              v-model="student.form.totalReimbursableMinutes"
              :error-messages="totalReimbursableMinutesErrors">
            </v-text-field>
          </v-flex>
        </v-layout>
        <medicaid-information
          ref="medicaidInformation"
          :student="student"
          :v="$v.student.form.medicaid"
          :medicaidArray="medicaidArray"
          :showTooltip="showTooltip" />
      </template>
      <v-layout row wrap :class="{'py-1': isMobile, 'pt-3': !isMobile}">
      <extended-service-information
          :action="action"
          :deliveryModeArray="deliveryModeArray"
          :groupTypeArray="groupTypeArray"
          :absentTypesArray="absentTypesArray"
          :absentReasonsArray="absentReasonsArray"
          :absentAlternativesArray="absentAlternativesArray"
          ref="extendedServiceInformation"
          :student="student"
          :supervisorFlagsArray="supervisorFlagsArray"
          :v="$v.student.form.serviceInformation"
          assessment-of-performance
          objective-note
          proposed-plan-and-target
          signature />
      </v-layout>
      <v-radio-group v-model="saveOptions" column>
        <!--if multiple students, not last student-->
        <v-tooltip :disabled="!showTooltip" top>
          <!--show save and next button if it is not the last student in the array-->
          <v-radio
            label="Save & Next Student"
            slot="activator"
            v-if="index < arrayLength - 1"
            value="next"
            color="primary">
          </v-radio>
          <span>Save and go to next student</span>
        </v-tooltip>
        <!--if only one student-->
        <v-tooltip :disabled="!showTooltip" top>
          <v-radio
            label="Save"
            slot="activator"
            v-if="arrayLength === 1 || index === arrayLength - 1"
            value="save"
            color="primary">
          </v-radio>
          <span>Save log</span>
        </v-tooltip>
        <!--if only one student-->
        <v-tooltip :disabled="!showTooltip" top>
          <v-radio
            label="Save & Clone"
            slot="activator"
            v-if="arrayLength === 1 && action !== 'edit'"
            value="copy"
            color="primary">
          </v-radio>
          <span>Save log and create duplicate</span>
        </v-tooltip>
      </v-radio-group>
      <v-alert :value="errorMsg" type="error">
        {{errorMsg}}
      </v-alert>
      <div>
        <v-tooltip :disabled="!showTooltip" top>
          <v-btn
            class="my-3"
            slot="activator"
            color="primary"
            round
            :block="isMobile"
            @click="submit(student)"
            :disabled="!saveOptions">
            Submit
          </v-btn>
          <span>Submit saved log</span>
        </v-tooltip>
        <v-btn
          class="cancel-button"
          round
          :block="isMobile"
          @click="onCancelButtonClicked">
          Cancel
        </v-btn>
      </div>
      <v-snackbar
        v-model="toaster"
        :timeout="timeout"
        color="cyan darken-2">
        {{toasterMsg}}
      </v-snackbar>
    </div>
    <bottom-nav></bottom-nav>
    <full-screen-loading-spinner v-if="showLoading"></full-screen-loading-spinner>
  </v-form>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import Swal from 'sweetalert2';
import api from '@/utils/api';
import utilityFunctions from '@/utils/functions';
import BottomNav from '@/components/bottom-nav';
import FullScreenLoadingSpinner from '@/components/full-screen-loading-spinner';
import SubmissionClass from '@/utils/service-log-submission-class';
import * as Constants from '@/constants';
import {
  isWithinOneYear,
  isBetweenStartAndEndDate,
  isNotFutureDate,
  areMinutesGreaterThanZero,
  isValidGroupSize,
  isValidLength,
  isProviderLocationRequired,
  isValidMinutes,
  isTotalMedicaidMinutesValid,
  isSessionTimeEndGreaterThanSessionTime,
  isTotalReimbursableMinutesValidForNonReimbursable,
  isTotalReimbursableMinutesValid,
  isValidSessionValueLength
} from '@/utils/validations';
import EventBus from '@/event-bus';
import StudentCard from '@/components/service-log/form/student-card';
import ServiceInformation from '@/components/service-log/form/service-information';
import MedicaidInformation from '@/components/service-log/form/medicaid-information';
import JWTHelper from '@/jwt-token';
import ExtendedServiceInformation from '@/components/service-log/form/extended-service-information';
/* eslint-disable */

export default {
  name: 'ServiceLogForm',
  components: {
    StudentCard,
    ServiceInformation,
    MedicaidInformation,
    ExtendedServiceInformation,
    BottomNav,
    FullScreenLoadingSpinner
  },
  props: {
    action: {},
    arrayLength: {},
    deliveryModeArray: {},
    groupTypeArray: {},
    index: {},
    loadData: {},
    medicaidArray: {},
    absentTypesArray: {},
    absentReasonsArray: {},
    absentAlternativesArray: {},
    resetValidations: {},
    serviceDeliveredArray: {},
    showTooltip: {},
    supervisorFlagsArray: {},
    student: {}
  },
  data() {
    return {
      errorMsg: '',
      // props for medicaid component
      saveOptions: '',
      // submitOptions
      save: false,
      saveAndNext: false,
      saveAndClone: false,
      showLoading: false,
      constants: Constants,
      toaster: false,
      toasterMsg: '',
      timeout: 2000,
      totalMinutesError: '',
      userFullName: JWTHelper.fullName(),
      isAdmin: JWTHelper.isAdmin()
    };
  },
  computed: {
    isMobile() {
      return this.$mq === 'mobile';
    },
    showMedicaid() {
      /* eslint-disable */
      const serviceInfo = this.student.form.serviceInformation;
      let needLevelCheck = !Constants.IS_ABSENT(serviceInfo.absentType)
      return serviceInfo && needLevelCheck;
    },
    totalReimbursableMinutesErrors() {
      const errors = [];
      const totalMinutes = this.$v.student.form.totalReimbursableMinutes;
      if (!totalMinutes || !totalMinutes.$dirty) return errors;
      if (!totalMinutes.isTotalReimbursableMinutesValidForNonReimbursable) errors.push('Total Reimbursable Minutes must be equal to 0 when Service Delivered is Consultation, Observation, or Parent Training.');
      if (!totalMinutes.isTotalReimbursableMinutesValid) errors.push('Total Reimbursable Minutes must be less than or equal to Service Length.');
      return errors;
    },
    studentBehaviorErrors() {
      const errors = [];
      const studentBehavior = this.$v.student.form.studentBehavior;
      if (!studentBehavior || !studentBehavior.$dirty) return errors;
      if (!studentBehavior.isValidStudentBehaviorLength) errors.push('Relevant Student Behavior Details must be less than 300 characters ');
      return errors;
    }
  },
  validations() {
    // truncated form if student is absent
    if (this.student.form.serviceInformation.absentType && Constants.IS_ABSENT_STU(this.student.form.serviceInformation.absentType)) {
      if (this.action === 'edit' && this.supervisorFlagsArray.canSupervise && this.supervisorFlagsArray.supervisorRequiredFlag) {
      return {
        student: {
          form: {
            totalMedicaidMinutes: {
              isTotalMedicaidMinutesValid
            },
            serviceInformation: {
              absentType: {
                // required
              },
              absentReason: {
                required
              },
              sessionDate: {
                required,
                isWithinOneYear,
                isBetweenStartAndEndDate,
                isNotFutureDate
              },
              location: {
                required
              },
              licenseAttestUserId: {
                required
              },
              supervisorUserId: {
                required
              }
            }
          }
        }
      };
      }
      else {
        return {
        student: {
          form: {
            totalMedicaidMinutes: {
              isTotalMedicaidMinutesValid
            },
            serviceInformation: {
              absentType: {
                // required
              },
              absentReason: {
                required
              },
              sessionDate: {
                required,
                isWithinOneYear,
                isBetweenStartAndEndDate,
                isNotFutureDate
              },
              location: {
                required
              },
              licenseAttestUserId: {
                required
              }
            }
          }
        }
      };
      }
    } // end student absent
    // truncated form if provider is absent and alternate service provided
    if (this.student.form.serviceInformation.absentType && Constants.IS_ABSENT_PROVIDER(this.student.form.serviceInformation.absentType) && Constants.IS_ALT_SERVICE(this.student.form.serviceInformation.absentAlternative)) {
      if (this.action === 'edit' && this.supervisorFlagsArray.canSupervise && this.supervisorFlagsArray.supervisorRequiredFlag) {
      return {
        student: {
          form: {
            totalMedicaidMinutes: {
              isTotalMedicaidMinutesValid
            },
            serviceInformation: {
              absentType: {
                // required
              },
              absentAlternative: {
                required
              },
              absentAlternativeDesc: {
                required
              },
              sessionDate: {
                required,
                isWithinOneYear,
                isBetweenStartAndEndDate,
                isNotFutureDate
              },
              location: {
                required
              },
              licenseAttestUserId: {
                required
              },
              supervisorUserId: {
                required
              }
            }
          }
        }
      };
      }
      else {
        return {
        student: {
          form: {
            totalMedicaidMinutes: {
              isTotalMedicaidMinutesValid
            },
            serviceInformation: {
              absentType: {
                // required
              },
              absentAlternative: {
                required
              },
              absentAlternativeDesc: {
                required
              },
              sessionDate: {
                required,
                isWithinOneYear,
                isBetweenStartAndEndDate,
                isNotFutureDate
              },
              location: {
                required
              },
              licenseAttestUserId: {
                required
              }
            }
          }
        }
      };
      }
    } // end provider absent and alternate service provided
    // truncated form if provider is absent and alternate service NOT provided
    if (this.student.form.serviceInformation.absentType && Constants.IS_ABSENT_PROVIDER(this.student.form.serviceInformation.absentType) && !Constants.IS_ALT_SERVICE(this.student.form.serviceInformation.absentAlternative)) {
      if (this.action === 'edit' && this.supervisorFlagsArray.canSupervise && this.supervisorFlagsArray.supervisorRequiredFlag) {
      return {
        student: {
          form: {
            totalMedicaidMinutes: {
              isTotalMedicaidMinutesValid
            },
            serviceInformation: {
              absentType: {
                // required
              },
              absentAlternative: {
                required
              },
              noServiceNotifFlag: {
                required
              },
              sessionDate: {
                required,
                isWithinOneYear,
                isBetweenStartAndEndDate,
                isNotFutureDate
              },
              location: {
                required
              },
              licenseAttestUserId: {
                required
              },
              supervisorUserId: {
                required
              }
            }
          }
        }
      };
      }
      else {
        return {
        student: {
          form: {
            totalMedicaidMinutes: {
              isTotalMedicaidMinutesValid
            },
            serviceInformation: {
              absentType: {
                // required
              },
              absentAlternative: {
                required
              },
              noServiceNotifFlag: {
                required
              },
              sessionDate: {
                required,
                isWithinOneYear,
                isBetweenStartAndEndDate,
                isNotFutureDate
              },
              location: {
                required
              },
              licenseAttestUserId: {
                required
              }
            }
          }
        }
      };
      }
    } // end provider absent and alternate service NOT provided
    // validate full form if absentType is null
    if (this.action === 'edit' && this.supervisorFlagsArray.canSupervise && this.supervisorFlagsArray.supervisorRequiredFlag){
      return {
      student: {
        form: {
          totalMedicaidMinutes: {
            isTotalMedicaidMinutesValid
          },
          totalReimbursableMinutes: {
            isTotalReimbursableMinutesValidForNonReimbursable,
            isTotalReimbursableMinutesValid
          },
          serviceInformation: {
            absentType: {
              // required
            },
            serviceDelivered: {
              required
            },
            sessionDate: {
              required,
              isWithinOneYear,
              isBetweenStartAndEndDate,
              isNotFutureDate
            },
            sessionTime: {
              required
            },
            sessionTimeEnd: {
              required,
              isSessionTimeEndGreaterThanSessionTime
            },
            sessionMinutes: {
              required,
              areMinutesGreaterThanZero,
              isValidMinutes
            },
            location: {
              required
            },
            groupSize: {
              isValidGroupSize,
              isValidLength
            },
            deliveryMode: {
              required
            },
            providerLocation: {
              isProviderLocationRequired
            },
            otSessionBehaviors: {
              required,
              isValidSessionValueLength
            },
            otSessionObjectives: {
              required,
            },
            otSessionProgressCodes: {
              required
            },
            /** TODO - TECH DEBT: Removed length validator on implementation sessions post shift to arrays to support
             * multi-values. Need to determine if user input is allowed and/or length limit requirements for validation.
             */
            otSessionImplementations: {
              required
            },
            licenseAttestUserId: {
              required
            },
            supervisorUserId: {
              required
            }
          },
          medicaid: {
            $each: {
              minutes: {
                required,
                areMinutesGreaterThanZero,
                isValidMinutes
              },
              procedure: {
                required
              }
            }
          }
        }
      }
    };
    }
    else {
      return {
      student: {
        form: {
          totalMedicaidMinutes: {
            isTotalMedicaidMinutesValid
          },
          totalReimbursableMinutes: {
            isTotalReimbursableMinutesValidForNonReimbursable,
            isTotalReimbursableMinutesValid
          },
          serviceInformation: {
            absentType: {
              // required
            },
            sessionDate: {
              required,
              isWithinOneYear,
              isBetweenStartAndEndDate,
              isNotFutureDate
            },
            sessionTime: {
              required
            },
            sessionTimeEnd: {
              required,
              isSessionTimeEndGreaterThanSessionTime
            },
            sessionMinutes: {
              required,
              areMinutesGreaterThanZero,
              isValidMinutes
            },
            serviceDelivered: {
              required
            },
            location: {
              required
            },
            groupSize: {
              isValidGroupSize,
              isValidLength
            },
            deliveryMode: {
              required
            },
            providerLocation: {
              isProviderLocationRequired
            },
            otSessionBehaviors: {
              required,
              isValidSessionValueLength
            },
            otSessionObjectives: {
              required
            },
            otSessionImplementations: {
              required,
            },
            otSessionProgressCodes: {
              required
            },
            licenseAttestUserId: {
              required
            }
          },
          medicaid: {
            $each: {
              minutes: {
                required,
                areMinutesGreaterThanZero,
                isValidMinutes
              },
              procedure: {
                required
              }
            }
          }
        }
      }
    };
    }

  },
  mounted() {
    EventBus.$on('reset-validations', this.resetForm);
  },
  methods: {
    calculateMinutes() {
      if (this.$refs.medicaidInformation) {
        this.$refs.medicaidInformation.calculateMinutes();
      }
    },
    onCancelButtonClicked() {
      if (this.action === 'edit' || this.action === 'copy') {
        this.$router.go(-1);
      } else {
        this.$emit('remove:student', this.student);
      }
    },
    handleTextTrimming(field, value) {
      if (value !== null && value !== undefined) {
        const trimmed = value.trim();
        this.student.form.serviceInformation[field] = trimmed;
      }
    },
    resetForm() {
      this.$v.student.form.$reset();
      this.errorMsg = '';
    },
    handleSubmit(res) {
      switch (this.saveOptions) {
        case 'next':
          // add the submission to localStorage to display later
          utilityFunctions.addSubmission(res.data.data);
          // remove student from selected-students array
          this.$emit('remove:student');
          this.$v.student.$reset();
          this.saveOptions = null;
          break;
        case 'copy':
          // copies the form for the user to submit again
          this.action = 'copy';
          this.student.form.serviceInformation.licenseAttestUserId = null;
          this.student.form.serviceInformation.licenseAttestDate = null;
          this.student.form.serviceInformation.licenseAttestName = null;
          this.student.form.serviceInformation.licenseAttestFlag = false;
          this.student.form.serviceInformation.sessionDate = '';
          this.saveOptions = null;
          utilityFunctions.addSubmission(res.data.data);
          EventBus.$emit('reset-validations');
          break;
        default:
          // default case is 'save', which saves the form and displays submissions
          utilityFunctions.addSubmission(res.data.data);
          this.$router.push('/history')
          break;
      }
    },
    showAlert() {
      return new Promise((resolve) => {
        // if there is a credential name, resolve right away
        if (this.student.form.serviceInformation.credentialName) {
          resolve();
        } else {
          const Toast = Swal.mixin({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 2500,
            timerProgressBar: true,
            onOpen: (toast) => {
              toast.addEventListener('mouseenter', Swal.stopTimer);
              toast.addEventListener('mouseleave', Swal.resumeTimer);
            },
            onClose: () => {
              resolve();
            }
          });
          Toast.fire({
            icon: 'warning',
            title: 'This provider has no associated credentials.',
            customClass: {
              popup: 'popup-class'
            }
          });
        }
      });
    },
    submitForm(student) {
      this.errorMsg = '';
      this.showLoading = true;
      const formSubmission = new SubmissionClass(student);
      this.showAlert()
        .then(() => {
          return api.post(Constants.OTSL_URL(), formSubmission);
        })
        .then((res) => {
          this.handleSubmit(res);
          const Toast = Swal.mixin({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 2500,
            timerProgressBar: true,
            onOpen: (toast) => {
              toast.addEventListener('mouseenter', Swal.stopTimer);
              toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
          });
          Toast.fire({
            icon: 'success',
            title: 'Service Log has been successfully submitted!',
            customClass: {
              popup: 'popup-class'
            }
          });
        })
        .catch((err) => {
          this.errorMsg = err.response.data.message;
        })
        .finally(() => {
          this.showLoading = false;
        });
    },
    editForm() {
      this.errorMsg = '';
      this.showLoading = true;
      const formSubmission = new SubmissionClass(this.student);
      const sessionId = this.$route.params.sessionId
      // add session id to body to update service log
      formSubmission.sessionId = sessionId;
      return api.put(Constants.OTSL_UPDATE_URL(sessionId), formSubmission)
        .then((res) => {
          this.handleSubmit(res);
        })
        .catch((err) => {
          this.errorMsg = err.response.data.message;
        })
        .finally(() => {
          this.showLoading = false;
          const Toast = Swal.mixin({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 2500,
            timerProgressBar: true,
            onOpen: (toast) => {
              toast.addEventListener('mouseenter', Swal.stopTimer);
              toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
          });
          Toast.fire({
            icon: 'success',
            title: 'Service Log has been successfully submitted!',
            customClass: {
              popup: 'popup-class'
            }
          });
        });
    },
    submit(student) {
      this.$v.student.form.$touch();
      if (!this.$v.student.form.$invalid) {
        this.$emit('form:submitted');
        if (this.action === 'edit') {
          let objectiveOtherError = false;
          this.student.form.serviceInformation.otSessionObjectives.forEach((objective) => {
              if (objective.value == "OTHER" && objective.objectiveOther == null) {
                objectiveOtherError = true;
              }
              else {
                objectiveOtherError = false;
              }
            });
            let implementationOtherError = false;
            let objectiveId = null;
            this.student.form.serviceInformation.otSessionImplementations.some((element) => {
              if (element.length > 0 && element[0].value === "OTHER") {
                objectiveId = element[0].objectiveId;
                return true;  // Exit the some loop when "OTHER" is found
              }
              return false;
            });
          if (objectiveId) {
            const matchingObjective = this.student.form.serviceInformation.otSessionObjectives.find((objective) => objective.id == objectiveId);
            if (matchingObjective && (matchingObjective.implementationOther === null || matchingObjective.implementationOther === "")) {
              implementationOtherError = true;
            } else {
              implementationOtherError = false;
            }
          } else {
            implementationOtherError = false;
          }
          let emptyObjective = false;
          this.student.form.serviceInformation.otSessionObjectives.forEach((element) => {
              if (element.value === '' || element.value === null) {
                emptyObjective = true;
                return true;  // Exit the some loop when "OTHER" is found
              }
              return false;
            });
            let emptyImplementation = false;
              for (const element of this.student.form.serviceInformation.otSessionImplementations) {
                if (element.length > 0 && (element[0].value === '' || element[0].value === null)) {
                  emptyImplementation = true;
                }
                else if (element.length === 0) {
                  emptyImplementation = true;
                } else {
                  emptyImplementation = false;
                }
              }
          if (!this.student.form.serviceInformation.nextSessionFocus && !this.student.form.serviceInformation.nextSessionGoal && !this.student.form.serviceInformation.absentType) {
            this.errorMsg = 'Either Next Session Focus or Next Session Goal is Required';
          }
          else if (!this.student.form.serviceInformation.otSessionProgressCodes[0].task && !this.student.form.serviceInformation.absentType) {
            this.errorMsg = 'Please correct the errors above and resubmit the form';
          }
          else if (emptyObjective && !this.student.form.serviceInformation.absentType) {
            this.errorMsg = 'Please correct the errors above and resubmit the form';
          }
          else if (emptyImplementation && !this.student.form.serviceInformation.absentType) {
            this.errorMsg = 'Please correct the errors above and resubmit the form';
          }
          else if (objectiveOtherError && !this.student.form.serviceInformation.absentType) {
            this.errorMsg = 'Other Objective Description is Required';
          }
          else if (implementationOtherError && !this.student.form.serviceInformation.absentType) {
            this.errorMsg = 'Other Implementation Description is Required';
          }
          else {
            this.editForm(student);
          }
        } else {
          let objectiveOtherError = false;
          this.student.form.serviceInformation.otSessionObjectives.forEach((objective) => {
              if (objective.value == "OTHER" && objective.objectiveOther == null) {
                objectiveOtherError = true;
              }
              else {
                objectiveOtherError = false;
              }
            });
            let implementationOtherError = false;
            let otherCount = 0;
            let objectiveOtherCount = 0;
            this.student.form.serviceInformation.otSessionImplementations.forEach((element) => {
              if (element.length > 0 && element[0].value === "OTHER") {
                otherCount++;
              }
            });
          if (otherCount > 0) {
            this.student.form.serviceInformation.otSessionObjectives.forEach((objective) => {
              if (objective.implementationOther === '' || objective.implementationOther === null) {
                objectiveOtherCount ++;
              }
            });
            if (otherCount > 0 && objectiveOtherCount > 0) {
              implementationOtherError = true;
            } else {
              implementationOtherError = false;
            }
          } else {
            implementationOtherError = false;
          }

          let emptyObjective = false;
          this.student.form.serviceInformation.otSessionObjectives.forEach((element) => {
              if (element.value === '' || element.value === null) {
                emptyObjective = true;
                return true;  // Exit the some loop when "OTHER" is found
              }
              return false;
            });
            let emptyImplementation = false;
              for (const element of this.student.form.serviceInformation.otSessionImplementations) {
                if (element.length > 0 && (element[0].value === '' || element[0].value === null)) {
                  emptyImplementation = true;
                } else {
                  emptyImplementation = false;
                }
              }
          if (!this.student.form.serviceInformation.nextSessionFocus && !this.student.form.serviceInformation.nextSessionGoal && !this.student.form.serviceInformation.absentType) {
            this.errorMsg = 'Either Next Session Focus or Next Session Goal is Required';
          }
          else if (!this.student.form.serviceInformation.otSessionProgressCodes[0].task === "" && !this.student.form.serviceInformation.absentType) {
            this.errorMsg = 'Please correct the errors above and resubmit the form';
          }
          else if (emptyImplementation && !this.student.form.serviceInformation.absentType) {
            this.errorMsg = 'Please correct the errors above and resubmit the form';
          }
          else if (emptyObjective && !this.student.form.serviceInformation.absentType) {
            this.errorMsg = 'Please correct the errors above and resubmit the form';
          }
          else if (objectiveOtherError) {
            this.errorMsg = 'Other Objective Description is Required';
          }
          else if (implementationOtherError) {
            this.errorMsg = 'Other Implementation Description is Required';
          }
          else {
            this.submitForm(student);
          }
        }
      } else if (!this.$v.student.form.totalMedicaidMinutes.isTotalMedicaidMinutesValid) {
        this.errorMsg = 'Service Length must equal sum of procedure minutes.';
      } else if (this.student.form.serviceInformation.licenseAttestUserId === null) {
        this.errorMsg = 'Provider Signature is Required';
      } else if (!this.student.form.serviceInformation.absentType && this.student.form.serviceInformation.otSessionProgressCodes[0].task === null) {
        this.errorMsg = 'Please correct the errors above and resubmit the form';
      } else {
        this.errorMsg = 'Please correct the errors above and resubmit the form';
      }
    }
  },
  watch: {
    'student.form.serviceInformation.sessionMinutes': {
      handler() {
        if (this.$v.student.form.totalMedicaidMinutes) {
          this.$v.student.form.totalMedicaidMinutes.$touch();
        }
      }
    },
    'student.form.totalReimbursableMinutes': {
      handler() {
        if (this.$v.student.form.totalReimbursableMinutes) {
          this.$v.student.form.totalReimbursableMinutes.$touch();
        }
      }
    },
    'student.form.totalMedicaidMinutes': {
      handler() {
        if (this.$v.student.form.totalMedicaidMinutes) {
          this.$v.student.form.totalMedicaidMinutes.$touch();
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
  .cancel-button {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1) !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/service-log/form/service-log-form.vue